import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Plus, Pencil, Trash2 } from "lucide-react";
import { PaginationBar, usePagination } from "@/components/Pagination";
import { ADMIN_MENU_TREE } from "@/components/Layout";

const PERM_FIELDS = ["view", "create", "edit", "delete", "print"];

const leavesOf = (n) => (n.children ? n.children.flatMap(leavesOf) : [n.key]);
const flattenTree = (nodes, depth = 0) =>
  nodes.flatMap((n) => [{ ...n, depth }, ...(n.children ? flattenTree(n.children, depth + 1) : [])]);

export function AdminRolesList() {
  const nav = useNavigate();
  const [roles, setRoles] = useState([]);
  const pg = usePagination(roles);

  const load = () => api.get("/admin/admin-roles").then(r => setRoles(r.data)).catch(e => toast.error(e.response?.data?.detail || "Load failed"));
  useEffect(() => { load(); }, []);

  const del = async (r) => {
    if (!confirm(`Delete role "${r.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/admin-roles/${r.id}`); toast.success("Role deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-roles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Admin Role</h1>
          <p className="text-muted-foreground">Platform admin roles and their permission scope.</p>
        </div>
        <Button size="sm" onClick={() => nav("/admin/admin-roles/new")} data-testid="new-admin-role-btn"><Plus className="w-4 h-4 mr-1"/> New Admin Role</Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageItems.map(r => (
                <TableRow key={r.id} data-testid={`admin-role-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name} {r.built_in && <Badge variant="outline" className="ml-2">System</Badge>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.description || "—"}</TableCell>
                  <TableCell><Badge variant={r.is_active ? "default" : "destructive"}>{r.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => nav(`/admin/admin-roles/${r.id}/edit`)} data-testid={`admin-role-edit-${r.id}`}>
                      <Pencil className="w-3.5 h-3.5"/>
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" disabled={r.built_in}
                      onClick={() => del(r)} data-testid={`admin-role-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!roles.length && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No roles</TableCell></TableRow>}
            </TableBody>
          </Table>
          <PaginationBar pg={pg}/>
        </CardContent>
      </Card>
    </div>
  );
}

function PermSection({ title, sub, nodes, perms, setPerms, testPrefix, locked = false }) {
  const allLeaves = useMemo(() => nodes.flatMap(leavesOf), [nodes]);
  const rows = useMemo(() => flattenTree(nodes), [nodes]);

  const setKeys = (keys, f, v) =>
    setPerms((p) => {
      const next = { ...p };
      keys.forEach((k) => { next[k] = { ...(next[k] || {}), [f]: v }; });
      return next;
    });
  const setAll = (v) =>
    setPerms((p) => {
      const next = { ...p };
      allLeaves.forEach((k) => { next[k] = Object.fromEntries(PERM_FIELDS.map((f) => [f, v])); });
      return next;
    });

  return (
    <div className="border border-border rounded-sm overflow-hidden" data-testid={`${testPrefix}-section`}>
      <div className="flex items-center justify-between gap-2 p-3 border-b border-border bg-secondary/40 flex-wrap">
        <div>
          <h3 className="text-sm uppercase tracking-wide font-bold" data-testid={`${testPrefix}-title`}>{title}</h3>
          <p className="text-xs text-muted-foreground">{sub}</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" data-testid={`${testPrefix}-select-all`} disabled={locked} onClick={() => setAll(true)}>Select All</Button>
          <Button type="button" size="sm" variant="outline" data-testid={`${testPrefix}-deselect-all`} disabled={locked} onClick={() => setAll(false)}>Deselect All</Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Function / Menu</TableHead>
            {PERM_FIELDS.map(f => <TableHead key={f} className="text-center capitalize w-16">{f}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(n => {
            const keys = leavesOf(n);
            const parent = !!n.children;
            return (
              <TableRow key={n.key} data-testid={`${testPrefix}-row-${n.key}`} className={parent ? "bg-secondary/30" : ""}>
                <TableCell className={parent ? "font-bold uppercase tracking-wide text-xs" : "font-medium text-sm"}
                  style={{ paddingLeft: 12 + n.depth * 22 }}>{n.label}</TableCell>
                {PERM_FIELDS.map(f => {
                  const count = keys.filter(k => perms[k]?.[f]).length;
                  const checked = count === keys.length;
                  const ind = count > 0 && !checked;
                  return (
                    <TableCell key={f} className="text-center">
                      <Checkbox
                        data-testid={`role-perm-${n.key}-${f}`}
                        checked={ind ? "indeterminate" : checked}
                        disabled={locked}
                        onCheckedChange={(v) => setKeys(keys, f, v === true)}/>
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export function AdminRoleForm() {
  const { rid } = useParams();
  const isEdit = !!rid;
  const nav = useNavigate();
  const [trees, setTrees] = useState({ platform_tree: [], menu_tree: ADMIN_MENU_TREE });
  const [role, setRole] = useState({ name: "", description: "", is_active: true, permissions: {}, built_in: false });
  const [isSaas, setIsSaas] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const setPerms = (updater) =>
    setRole(prev => ({ ...prev, permissions: typeof updater === "function" ? updater(prev.permissions) : updater }));

  useEffect(() => {
    api.get("/admin/admin-permission-tree").then(({ data }) => {
      const menuTree = ADMIN_MENU_TREE; // auto-derived from the live navigation — single source of truth
      setTrees({ platform_tree: data.platform_tree, menu_tree: menuTree });
      const empty = {};
      [...data.platform_tree.flatMap(leavesOf), ...menuTree.flatMap(leavesOf)]
        .forEach(k => { empty[k] = Object.fromEntries(PERM_FIELDS.map(f => [f, false])); });
      if (!isEdit) {
        setRole(prev => ({ ...prev, permissions: { ...empty, ...prev.permissions } }));
        setLoading(false); return;
      }
      api.get("/admin/admin-roles").then(({ data: roles }) => {
        const r = roles.find(x => x.id === rid);
        if (!r) { toast.error("Role not found"); nav("/admin/admin-roles"); return; }
        setIsSaas(r.key === "super_admin");
        setRole({ name: r.name, description: r.description || "", is_active: r.is_active,
                  built_in: r.built_in, key: r.key, permissions: { ...empty, ...(r.permissions || {}) } });
        setLoading(false);
      });
    }).catch(() => { toast.error("Failed to load"); nav("/admin/admin-roles"); });
  }, [rid]);

  const save = async (e) => {
    e?.preventDefault();
    if (!role.name.trim()) { toast.error("Role name required"); return; }
    setBusy(true);
    try {
      const payload = { name: role.name, description: role.description, is_active: role.is_active, permissions: role.permissions };
      if (isEdit) await api.patch(`/admin/admin-roles/${rid}`, payload);
      else await api.post("/admin/admin-roles", payload);
      toast.success(isEdit ? "Role updated" : "Role created");
      nav("/admin/admin-roles");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const allLeafKeys = [...trees.platform_tree.flatMap(leavesOf), ...ADMIN_MENU_TREE.flatMap(leavesOf)];
  const effPerms = isSaas
    ? Object.fromEntries(allLeafKeys.map(k => [k, Object.fromEntries(PERM_FIELDS.map(f => [f, true]))]))
    : role.permissions;

  return (
    <div className="p-6 max-w-5xl" data-testid="admin-role-form-page">
      <Link to="/admin/admin-roles" data-testid="admin-role-back" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft className="w-3 h-3"/> Back to Admin Roles
      </Link>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{isEdit ? "Edit Admin Role" : "New Admin Role"}</CardTitle>
          <CardDescription>Role name, description, and the two permission sections — saved together.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-6" data-testid="admin-role-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role Name *</Label>
                <Input data-testid="role-name" value={role.name} onChange={(e) => setRole({ ...role, name: e.target.value })}
                  disabled={role.built_in} required />
                {role.built_in && <p className="text-xs text-muted-foreground mt-1">System roles cannot be renamed.</p>}
              </div>
              <div className="flex items-end justify-between border border-border rounded-sm px-3 py-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Active</Label>
                <Switch data-testid="role-active" checked={role.is_active} onCheckedChange={(v) => setRole({ ...role, is_active: v })}/>
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea data-testid="role-description" value={role.description} onChange={(e) => setRole({ ...role, description: e.target.value })} rows={2} />
              </div>
            </div>

            {isSaas && (
              <div data-testid="saas-role-locked-banner" className="border border-primary/40 bg-primary/10 text-primary rounded-sm px-4 py-3 text-sm">
                The SaaS Admin role always has full, permanent access to every menu, submenu and function. All permissions below are shown enabled and cannot be changed.
              </div>
            )}

            <PermSection
              title="Section 1 — Platform Function Permissions"
              sub="Platform Admin panel functions and pages. Checking a parent checks all its children."
              nodes={trees.platform_tree} perms={effPerms} setPerms={setPerms} testPrefix="role-s1" locked={isSaas}/>

            <PermSection
              title="Section 2 — Menu and Sub-Menu Permissions"
              sub="Auto-detected from the application navigation — any menu, submenu, or page added to the app appears here automatically. Unchecked View = hidden from navigation."
              nodes={trees.menu_tree} perms={effPerms} setPerms={setPerms} testPrefix="role-s2" locked={isSaas}/>

            <div className="flex gap-2">
              <Button data-testid="role-save" type="submit" disabled={busy}>{busy ? "Saving…" : isEdit ? "Save Changes" : "Create Role"}</Button>
              <Button data-testid="role-cancel" type="button" variant="outline" onClick={() => nav("/admin/admin-roles")}>Cancel</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
